import { useState } from "react";
import useApi from "./useApi";
import { API_ROUTES } from "lib/constants/api-constants";

interface RoomReservationState {
  loading: boolean;
  error: string | null;

  byClinicAndDate: (
    clinicId: string,
    shortDate: string,
    onSuccess?: (details: RoomReservation[]) => void
  ) => void;

  byPractitioner: (
    practitionerId: string,
    onSuccess?: (details: RoomReservation[]) => void
  ) => void;

  byPractitionerSubscription: (
    practitionerSubscriptionId: string,
    onSuccess?: (details: RoomReservation[]) => void
  ) => void;

  byPractitionerAndClinic: (
    practitionerId: string,
    clinicId: string,
    onSuccess?: (details: RoomReservation[]) => void
  ) => void;

  updateRoomReservation: (
    roomReservation: RoomReservation
  ) => Promise<RoomReservation | { error: string } | undefined>;

  deleteRoomReservation: (
    roomReservationId: string,
    onSuccess?: () => void
  ) => void;
}

const useRoomReservations = (): RoomReservationState => {
  const [error, setError] = useState<string | null>();
  const {
    getData: getRoomReservationDetailsRequest,
    loading: getRoomReservationDetailsRequestLoading,
  } = useApi<RoomReservation[]>();

  const {
    putData: updateReservationRequest,
    loading: updateReservationRequestLoading,
  } = useApi<RoomReservation>();

  const {
    deleteData: deleteRoomReservationRequest,
    loading: deleteRoomReservationRequestLoading,
  } = useApi<GeneralAPIResponse>();

  const byClinicAndDate = async (
    clinicId: string,
    shortDate: string,
    onSuccess?: (details: RoomReservation[]) => void
  ) => {
    setError(null);
    const endPoint = API_ROUTES.ROOM_RESERVATIONS.byClinicAndDate
      .replace("{{CLINIC_ID}}", clinicId)
      .replace("{{SHORT_DATE}}", shortDate);
    await getRoomReservationDetailsRequest(endPoint, onSuccess, setError);
  };

  const byPractitioner = async (
    practitionerId: string,
    onSuccess?: (details: RoomReservation[]) => void
  ) => {
    setError(null);
    const endPoint = API_ROUTES.ROOM_RESERVATIONS.byPractitionerId.replace(
      "{{PRACTITIONER_ID}}",
      practitionerId
    );
    await getRoomReservationDetailsRequest(endPoint, onSuccess, setError);
  };

  const byPractitionerAndClinic = async (
    practitionerId: string,
    clinicId: string,
    onSuccess?: (details: RoomReservation[]) => void
  ) => {
    setError(null);
    const endPoint = API_ROUTES.ROOM_RESERVATIONS.byPractitionerAndClinic
      .replace("{{PRACTITIONER_ID}}", practitionerId)
      .replace("{{CLINIC_ID}}", clinicId);
    await getRoomReservationDetailsRequest(endPoint, onSuccess, setError);
  };

  const byPractitionerSubscription = async (
    practitionerSubscriptionId: string,
    onSuccess?: (details: RoomReservation[]) => void
  ) => {
    setError(null);
    const endPoint = API_ROUTES.ROOM_RESERVATIONS.byPractitionerSubscription(
      practitionerSubscriptionId
    );
    await getRoomReservationDetailsRequest(endPoint, onSuccess, setError);
  };

  const updateRoomReservation = async (
    roomReservation: RoomReservation
  ): Promise<RoomReservation | { error: string } | undefined> => {
    try {
      const result = await updateReservationRequest(
        API_ROUTES.ROOM_RESERVATIONS.updateReservation,
        roomReservation
      );

      if (result && !(result as any).error) {
        return result;
      } else {
        const errorMsg =
          (result as any)?.error || "Unknown error updating room reservation";
        // Don't trigger re-render here — just return the error
        return { error: errorMsg };
      }
    } catch (err: any) {
      const errorMsg =
        err?.message || "Unknown error updating room reservation";
      // Don't call setError() here
      return { error: errorMsg };
    }
  };

  const deleteRoomReservation = async (
    roomReservationId: string,
    onSuccess?: () => void
  ) => {
    setError(null);

    await deleteRoomReservationRequest(
      API_ROUTES.ROOM_RESERVATIONS.deleteReservation(roomReservationId),
      {},
      onSuccess
    );
  };

  return {
    loading:
      getRoomReservationDetailsRequestLoading ||
      updateReservationRequestLoading ||
      deleteRoomReservationRequestLoading,
    error: error,
    byClinicAndDate,
    byPractitioner,
    byPractitionerAndClinic,
    byPractitionerSubscription,
    updateRoomReservation,
    deleteRoomReservation,
  };
};

export default useRoomReservations;
